import React from "react";
import Tooltip from "@mui/material/Tooltip";

export interface LocalTimeProps {
	utcDate?: string;
	children: React.ReactElement;
}

/**
 * Formats a given UTC date string to the user's local time and displays the UTC offset.
 *
 * @param utcDate - The UTC date string to format. It should be in the ISO 8601 format,
 *   e.g., "2025-09-25T07:10:45.828226Z".
 * @returns A formatted string showing the local time and UTC offset.
 */
const formatUTCDate = (utcDate: string): [string, string] => {
	try {
		const normalized = utcDate.endsWith("Z") ? utcDate : `${utcDate}Z`;
		const date = new Date(normalized);

		if (isNaN(date.getTime())) {
			return ["Invalid", "Date format"];
		}

		const utcTime = new Intl.DateTimeFormat(
			Intl.DateTimeFormat().resolvedOptions().locale,
			{
				timeZone: "UTC",
				hour: "2-digit",
				minute: "2-digit",
				hour12: false,
			}
		).format(date);

		const offsetMinutes = date.getTimezoneOffset();
		const offsetHours = -(offsetMinutes / 60);
		const offsetSign = offsetHours >= 0 ? "+" : "-";
		const offsetString = `UTC ${offsetSign}${Math.abs(offsetHours)}`;

		return [`Your local time (${offsetString})`, `UTC: ${utcTime}`];
	} catch {
		return ["Invalid", "Date format"];
	}
};

const LocalTime: React.FC<LocalTimeProps> = ({ utcDate, children }) => {
	if (utcDate) {
		const formattedDate = formatUTCDate(utcDate);
		return (
			<Tooltip
				title={
					<React.Fragment>
						{formattedDate[0]} <br />
						{formattedDate[1]}
					</React.Fragment>
				}
				placement="right-start"
			>
				{children}
			</Tooltip>
		);
	}
	return <></>;
};

export default LocalTime;
